import React from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

export interface FilterField {
  name: string;
  type: 'select' | 'dateRange' | 'numberRange' | string;
  label: string;
  options?: Array<{ value: string; label: string }>;
}

export interface AdvancedFiltersProps {
  filters: Record<string, any>;
  onFilterChange: (filters: Record<string, any>) => void;
  fields: FilterField[];
  showTaxCategories?: boolean;
  showFundTypes?: boolean;
}

// Define the available filters
const availableFilters = {
  types: [
    { value: '', label: 'All Types' },
    { value: 'ASSET', label: 'Assets' },
    { value: 'LIABILITY', label: 'Liabilities' },
    { value: 'EQUITY', label: 'Equity' },
    { value: 'REVENUE', label: 'Revenue' },
    { value: 'EXPENSE', label: 'Expenses' }
  ],
  categories: [
    { value: '', label: 'All Categories' },
    { value: 'OPERATING', label: 'Operating' },
    { value: 'RESTRICTED', label: 'Restricted' },
    { value: 'PROGRAM', label: 'Program' }
  ],
  taxCategories: [
    { value: '', label: 'All Tax Categories' },
    { value: '501c3', label: '501(c)(3)' },
    { value: '501c4', label: '501(c)(4)' }
  ],
  fundTypes: [
    { value: '', label: 'All Fund Types' },
    { value: 'OPERATING', label: 'Operating' },
    { value: 'RESTRICTED', label: 'Restricted' },
    { value: 'ENDOWMENT', label: 'Endowment' }
  ]
};

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  filters = {},
  onFilterChange,
  fields = [],
  showTaxCategories = false,
  showFundTypes = false
}) => {
  const handleChange = (field: string) => (event: any) => {
    onFilterChange({
      ...filters,
      [field]: event.target.value
    });
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      {fields?.map(field => (
        <FormControl key={field.name} sx={{ minWidth: 120 }}>
          <InputLabel>{field.label}</InputLabel>
          {field.type === 'select' ? (
            <Select
              value={filters[field.name] || ''}
              onChange={handleChange(field.name)}
              label={field.label}
            >
              {field.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <InputLabel>{field.label}</InputLabel>
          )}
        </FormControl>
      ))}

      {showTaxCategories && (
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Tax Category</InputLabel>
          <Select
            value={filters.taxCategory || ''}
            onChange={handleChange('taxCategory')}
            label="Tax Category"
          >
            {availableFilters.taxCategories.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {showFundTypes && (
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Fund Type</InputLabel>
          <Select
            value={filters.fundType || ''}
            onChange={handleChange('fundType')}
            label="Fund Type"
          >
            {availableFilters.fundTypes.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

export default AdvancedFilters; 